import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { Loader2, ZoomIn, ZoomOut, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import renderMathInElement from 'katex/dist/contrib/auto-render.js';
import 'katex/dist/katex.min.css';

/**
 * Renders a Mermaid diagram from a string definition.
 * Now includes robust error handling and fallback rendering to ensure
 * the component never completely fails to render.
 * 
 * @param {string} chart - The Mermaid chart definition string
 * @param {string} className - Optional className
 */
const MermaidRenderer = ({ chart, className = '' }) => {
    const containerRef = useRef(null);
    const viewportRef = useRef(null);
    const [svg, setSvg] = useState('');
    const [error, setError] = useState(null);
    const [fallbackChart, setFallbackChart] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const panStartRef = useRef({ x: 0, y: 0 });
    const offsetStartRef = useRef({ x: 0, y: 0 });
    const idRef = useRef(`mermaid-${Math.random().toString(36).substr(2, 9)}`);

    useEffect(() => {
        // Initialize mermaid with error handling
        try {
            mermaid.initialize({
                startOnLoad: false,
                theme: 'base',
                securityLevel: 'loose',
                fontFamily: 'Inter, sans-serif',
                themeVariables: {
                    primaryColor: '#ffffff',
                    primaryTextColor: '#0f172a',
                    primaryBorderColor: '#3b82f6', // Bright Blue
                    lineColor: '#64748b', // Slate 500
                    secondaryColor: '#f0f9ff',
                    tertiaryColor: '#ffffff',
                    mainBkg: '#ffffff',
                    nodeBorder: '#3b82f6',
                    clusterBkg: '#f8fafc',
                    clusterBorder: '#cbd5e1',
                    defaultLinkColor: '#64748b',
                    fontFamily: 'Inter, sans-serif',
                },
                flowchart: {
                    curve: 'basis', // Smooth curves
                    nodeSpacing: 50,
                    rankSpacing: 50,
                    htmlLabels: true,
                    padding: 15,
                    useMaxWidth: true,
                }
            });
        } catch (initErr) {
            console.error('Mermaid initialization error:', initErr);
        }
    }, []);

    useEffect(() => {
        const renderChart = async () => {
            if (!chart) {
                setIsLoading(false);
                setSvg('');
                setError(null);
                return;
            }

            // Only show loader if we don't have a previous SVG, preventing flashing on keystrokes
            if (!svg) {
                setIsLoading(true);
            }
            setError(null);

            try {
                // Sanitize chart input - trim whitespace and ensure it's a string
                const sanitizedChart = typeof chart === 'string' ? chart.trim() : String(chart);

                if (!sanitizedChart) {
                    setIsLoading(false);
                    return;
                }

                // Stage 1: Attempt standard validation & render
                const id = idRef.current;

                try {
                    await mermaid.parse(sanitizedChart);
                    const { svg } = await Promise.race([
                        mermaid.render(id, sanitizedChart),
                        new Promise((_, reject) => setTimeout(() => reject(new Error('Render timeout')), 10000))
                    ]);
                    setSvg(svg);
                    setError(null);
                } catch (parseOrRenderErr) {
                    console.warn('Mermaid initial parse/render failed. Attempting auto-healing regex...', parseOrRenderErr);

                    // Stage 2: Auto-Healing Scrub
                    // AI often hallucinates illegal characters inside Node IDs (e.g. A-1[Start] or Object.Name[Title]).
                    // We strip hyphens, dots, and spaces explicitly OUTSIDE of the bracketed label definitions.
                    let scrubbedChart = sanitizedChart
                        // Strip markdown list bullets from the beginning of lines (e.g. LLM outputs "- A --> B")
                        .replace(/^[*-]\s+/gm, '')
                        // Ensure node definitions like A.1[Label] become A1[Label]
                        .replace(/([A-Za-z0-9_-]+)([\s.\-]+)(?=\[)/g, (match, prefix) => prefix.replace(/[\s.\-]+/g, ''))
                        // Ensure relationships like A-1 --> B.2 become A1 --> B2
                        .replace(/([A-Za-z0-9_-]+)(?=\s*-->)/g, (match) => match.replace(/[\s.\-]+/g, ''))
                        .replace(/(?<=-->\s*)([A-Za-z0-9_-]+)/g, (match) => match.replace(/[\s.\-]+/g, ''))
                        // INTERCEPT RESERVED KEYWORDS: 'end', 'start', 'subgraph' are illegal as node IDs.
                        // Append '_node' if they are used as node definitions or edge targets/sources.
                        .replace(/\b(end|subgraph|start)\b(?=\s*\[)/gi, '$1_node')
                        .replace(/\b(end|subgraph|start)\b(?=\s*-->)/gi, '$1_node')
                        .replace(/(?<=-->\s*)\b(end|subgraph|start)\b/gi, '$1_node')
                        // Sometimes AI puts LaTeX with backslashes and braces directly into node labels which breaks Mermaid parser
                        // We replace backslashes with double backslashes inside labels, BUT we must protect KaTeX delimiters: \[ \] \( \)
                        // so Mermaid accepts them as strings, allowing KaTeX to read them later when rendered in the DOM
                        .replace(/\["([^"]*)"\]/g, (match, innerText) => {
                            let safe = innerText
                                .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, ' $1 / $2 ') // Prevent \frac from breaking parser
                                .replace(/\\(?![\[\]()])/g, '\\\\'); // Escape backslashes UNLESS they are part of a math delimiter
                            return `["${safe}"]`;
                        })
                        // Also catch labels that aren't wrapped in quotes, e.g. A[Some $ \frac{1}{2} $ Math]
                        // We must be careful not to match the node ID itself, only the contents of the brackets
                        .replace(/\[([^"\]]+)\]/g, (match, innerText) => {
                            let safe = innerText
                                .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, ' $1 / $2 ') // Prevent \frac from breaking parser
                                .replace(/\\(?![\[\]()])/g, '\\\\'); // Escape backslashes UNLESS they are part of a math delimiter
                            return `[${safe}]`;
                        });

                    try {
                        await mermaid.parse(scrubbedChart);
                        const { svg } = await Promise.race([
                            mermaid.render(id + '-retry', scrubbedChart),
                            new Promise((_, reject) => setTimeout(() => reject(new Error('Render timeout')), 10000))
                        ]);
                        setSvg(svg);
                        setError(null);
                    } catch (retryErr) {
                        // Stage 3: Graceful Fallback
                        // If it's structurally too broken to salvage, don't crash. Show the raw code snippet.
                        // We only show the error explicitly if we don't already have a valid SVG on screen.
                        // This prevents flashing errors while the LLM is mid-generation of a valid diagram.
                        if (!svg) {
                            console.error('Mermaid auto-healing failed. Falling back to explicit code display.', retryErr);
                            setError('Diagram Syntax Error');
                            setFallbackChart(sanitizedChart);
                            setSvg('');
                        }
                    }
                }
            } catch (err) {
                console.error('Fatal initialization error in Mermaid bounds:', err);
                if (!svg) {
                    setSvg('');
                    setFallbackChart(chart);
                    setError('Fatal Render Error');
                }
            } finally {
                setIsLoading(false);
            }
        };

        renderChart();
    }, [chart, refreshKey]);

    // Apply KaTeX rendering to Mermaid labels after SVG is injected
    useEffect(() => {
        if (!svg || !containerRef.current) return;

        // Explicitly set the SVG here instead of dangerouslySetInnerHTML 
        // This prevents React from overwriting KaTeX modifications when zooming/panning triggers a re-render
        containerRef.current.innerHTML = svg;

        // Force the SVG to stay bounded so it doesn't push off-screen during reflows
        const svgEl = containerRef.current.querySelector('svg');
        if (svgEl) {
            svgEl.style.maxWidth = '100%';
            svgEl.style.height = 'auto';
        }

        // Give browser a tick to paint the SVG DOM structure
        const timerId = setTimeout(() => {
            if (containerRef.current) {
                try {
                    renderMathInElement(containerRef.current, {
                        delimiters: [
                            { left: '$$', right: '$$', display: true },
                            { left: '\\[', right: '\\]', display: true },
                            { left: '$', right: '$', display: false },
                            { left: '\\(', right: '\\)', display: false }
                        ],
                        output: 'html',
                        throwOnError: false,
                        strict: false,
                        trust: true
                    });
                } catch (e) {
                    console.error('KaTeX rendering error within Mermaid:', e);
                }
            }
        }, 50);
        return () => clearTimeout(timerId);
    }, [svg]);

    const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

    const handleZoomIn = () => setScale(prev => clamp(prev + 0.2, 0.5, 3));
    const handleZoomOut = () => setScale(prev => clamp(prev - 0.2, 0.5, 3));
    const handleReset = () => {
        setScale(1);
        setOffset({ x: 0, y: 0 });
        setRefreshKey(prev => prev + 1);
    };

    const getClientPoint = (e) => {
        if (e?.touches?.[0]) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
        return { x: e.clientX, y: e.clientY };
    };

    const handlePanStart = (e) => {
        // Only left click for mouse
        if (e.type === 'mousedown' && e.button !== 0) return;
        setIsPanning(true);
        const pt = getClientPoint(e);
        panStartRef.current = pt;
        offsetStartRef.current = { ...offset };
    };

    const handlePanMove = (e) => {
        if (!isPanning) return;
        const pt = getClientPoint(e);
        const dx = pt.x - panStartRef.current.x;
        const dy = pt.y - panStartRef.current.y;
        setOffset({ x: offsetStartRef.current.x + dx, y: offsetStartRef.current.y + dy });
    };

    const handlePanEnd = () => setIsPanning(false);

    useEffect(() => {
        if (!isPanning) return;
        const move = (e) => handlePanMove(e);
        const up = () => handlePanEnd();
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', up);
        window.addEventListener('touchmove', move, { passive: false });
        window.addEventListener('touchend', up);
        return () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', up);
            window.removeEventListener('touchmove', move);
            window.removeEventListener('touchend', up);
        };
    }, [isPanning]);

    const handleWheel = (e) => {
        // support ctrl/cmd pinch-zoom as browser default
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        const delta = e.deltaY;
        setScale((prev) => clamp(prev + (delta < 0 ? 0.12 : -0.12), 0.5, 3));
    };

    return (
        <div className={`flex flex-col items-center bg-white rounded-lg border shadow-sm my-4 overflow-hidden ${className}`}>
            {/* Controls */}
            <div className="w-full flex justify-between items-center px-4 py-2 border-b bg-gray-50/50">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Interactive Diagram</span>
                <div className="flex items-center gap-1">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                        onClick={handleZoomOut}
                        title="Zoom Out"
                    >
                        <ZoomOut className="h-3.5 w-3.5" />
                    </Button>
                    <span className="text-xs w-12 text-center text-gray-500">{Math.round(scale * 100)}%</span>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                        onClick={handleZoomIn}
                        title="Zoom In"
                    >
                        <ZoomIn className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 ml-2 text-slate-600 hover:text-slate-900 hover:bg-slate-200/60"
                        onClick={handleReset}
                        title="Reset"
                    >
                        <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>

            {/* Diagram Area */}
            <div
                ref={viewportRef}
                className="w-full overflow-hidden p-6 flex justify-center min-h-[200px] relative bg-white"
                onMouseDown={handlePanStart}
                onTouchStart={handlePanStart}
                onWheel={handleWheel}
                style={{ touchAction: 'none', cursor: isPanning ? 'grabbing' : 'grab' }}
                title="Drag to pan, scroll to zoom"
            >
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 transition-opacity">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                )}

                {svg && (
                    <div
                        ref={containerRef}
                        className="transition-transform duration-200 origin-center"
                        style={{
                            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                            width: 'max-content',
                            maxWidth: '100%',
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    />
                )}

                {error && fallbackChart && !svg && (
                    <div className="w-full h-full flex flex-col items-center justify-center p-4">
                        <div className="text-red-500 font-medium mb-3 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                            {error}
                        </div>
                        <p className="text-sm text-gray-500 mb-4 px-4 text-center">
                            The AI generated invalid flowchart syntax. Below is the raw data it attempted to draw:
                        </p>
                        <div className="w-full max-w-2xl bg-slate-900 rounded-md p-4 overflow-x-auto text-left">
                            <pre className="text-xs text-slate-300 font-mono whitespace-pre-wrap">
                                {fallbackChart}
                            </pre>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MermaidRenderer;
